import type { OpMessagePayload, PointerPayload, RoomStatePayload, SyncOpViewPayload } from "./envelope";
import { roleOfIdentity } from "./envelope";
import {
  addLocal,
  applyRemote,
  confirmLocal,
  emptyOpLog,
  mergeServerOps,
  rejectLocal,
  type OpLogState,
} from "./annotations/opLog";
import type { OpKind, OpPayload, SyncOp } from "./annotations/shapes";
import {
  hidePointer,
  prunePointers as prunePointerStore,
  receivePointer,
  type PointerStore,
} from "./pointer/pointerModel";

/**
 * syncReducer - the client's trust and reconciliation rules as pure code.
 *
 * D-03: a `serverState` is only ever applied when the room is unknown or the incoming
 * `stateVersion` is not older than the one already held - an older broadcast racing in after a
 * newer one is a no-op, never a regression. The stored room never keeps its own `ops`/`hasMoreOps`
 * - those are applied separately through `serverOps`/`mergeServerOps`, which is the single op
 * merge path regardless of where the ops came from (D-09).
 *
 * D-12: a remote op's author is always the topic listener's sender identity (`roleOfIdentity`),
 * never a field read out of the message payload. A customer (`enduser-*`) op is applied only
 * while the room says `customerMayAnnotate`; a worker op is always applied; an op from a sender
 * whose identity classifies as neither is dropped.
 */

export type SyncState = {
  room: RoomStatePayload | null;
  opLog: OpLogState;
  pointers: PointerStore;
  lastError: string | null;
};

export function initialSyncState(): SyncState {
  return { room: null, opLog: emptyOpLog(), pointers: new Map(), lastError: null };
}

export type SyncAction =
  | { type: "serverState"; room: RoomStatePayload }
  | { type: "serverOps"; ops: SyncOpViewPayload[]; nowMs: number }
  | { type: "remoteOp"; senderIdentity: string; payload: OpMessagePayload; nowMs: number }
  | { type: "localOp"; op: SyncOp; nowMs: number }
  | { type: "localOpConfirmed"; opId: string; seq: number }
  | { type: "localOpRejected"; opId: string; message: string }
  | { type: "remotePointer"; senderIdentity: string; payload: PointerPayload; nowMs: number }
  | { type: "remotePointerHide"; senderIdentity: string }
  | { type: "prunePointers"; nowMs: number }
  | { type: "clearError" };

const KNOWN_OP_KINDS: ReadonlySet<string> = new Set<OpKind>([
  "ShapeAdd",
  "ShapeUpdate",
  "ShapeDelete",
  "ShapeRestore",
]);

/** Parses one server op view into a SyncOp, or null when the payload/kind cannot be trusted. */
export function toSyncOp(view: SyncOpViewPayload): SyncOp | null {
  if (!KNOWN_OP_KINDS.has(view.kind)) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(view.payload);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return null;
  }
  return {
    opId: view.opId,
    frameId: view.frameId,
    kind: view.kind as OpKind,
    payload: parsed as OpPayload,
    authorIdentity: view.authorIdentity,
    undoOf: view.undoOf,
    redoOf: view.redoOf,
    seq: view.seq,
  };
}

export function syncReducer(state: SyncState, action: SyncAction): SyncState {
  switch (action.type) {
    case "serverState": {
      const incoming = action.room;
      if (state.room !== null && incoming.stateVersion < state.room.stateVersion) {
        return state;
      }
      return { ...state, room: { ...incoming, ops: [], hasMoreOps: false } };
    }

    case "serverOps": {
      const ops = action.ops.map(toSyncOp).filter((op): op is SyncOp => op !== null);
      return { ...state, opLog: mergeServerOps(state.opLog, ops, action.nowMs) };
    }

    case "remoteOp": {
      const role = roleOfIdentity(action.senderIdentity);
      if (role === "unknown") {
        return state;
      }
      if (role === "customer" && !(state.room?.customerMayAnnotate ?? false)) {
        return state;
      }
      const op: SyncOp = {
        opId: action.payload.opId,
        frameId: action.payload.frameId,
        kind: action.payload.kind,
        payload: action.payload.payload as OpPayload,
        authorIdentity: action.senderIdentity,
        undoOf: action.payload.undoOf,
        redoOf: action.payload.redoOf,
        seq: null,
      };
      return { ...state, opLog: applyRemote(state.opLog, op, action.nowMs) };
    }

    case "localOp": {
      return { ...state, opLog: addLocal(state.opLog, action.op, action.nowMs) };
    }

    case "localOpConfirmed": {
      return { ...state, opLog: confirmLocal(state.opLog, action.opId, action.seq) };
    }

    case "localOpRejected": {
      return { ...state, opLog: rejectLocal(state.opLog, action.opId), lastError: action.message };
    }

    case "remotePointer": {
      return {
        ...state,
        pointers: receivePointer(state.pointers, action.senderIdentity, action.payload, action.nowMs),
      };
    }

    case "remotePointerHide": {
      return { ...state, pointers: hidePointer(state.pointers, action.senderIdentity) };
    }

    case "prunePointers": {
      return { ...state, pointers: prunePointerStore(state.pointers, action.nowMs) };
    }

    case "clearError": {
      return { ...state, lastError: null };
    }

    default: {
      const exhaustive: never = action;
      return exhaustive;
    }
  }
}

/**
 * newUuid - a v4 UUID for client-generated opIds (D-09). Uses `crypto.randomUUID` when
 * available, else builds one from `crypto.getRandomValues` - an insecure context (plain http on
 * a phone) can still have the latter without the former.
 */
export function newUuid(): string {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
